import {
  Button,
  Group,
  Icon,
  Popover,
  Stack,
  Switch,
  Text,
  desktopFirstQuery,
} from '@inflearn/ds-react-next';
import { faLink, faShare } from '@inflearn/pro-regular-svg-icons';
import { useMediaQuery } from '@mantine/hooks';
import { isBoolean } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { UCC_URL } from '../../../constants/urls';
import { useCopyShortLink } from '../../../hooks/useCopyShortLink';
import useRouterQueryId from '../../../hooks/useRouterQueryId';
import useNotePublishMutation from '../../../mutations/useNotePublishMutation';
import useClientAccountProfileQuery from '../../../queries/useClientAccountProfileQuery';
import useNoteIdMetadataQuery from './hooks/queries/useNoteIdMetadataQuery';
import { useTrackNoteShareEvent } from './hooks/useTrackNoteDetailMixpanelEvent';

export function SharedButton() {
  const { t } = useTranslation();
  const courseNoteId = useRouterQueryId();

  const { data: profile } = useClientAccountProfileQuery();
  const { data: metadata } = useNoteIdMetadataQuery(courseNoteId);
  const isWritable = metadata?.isWritable;
  const isPublic = metadata?.isPublic;

  const [opened, setOpened] = useState(false);

  const isSmallerThanDesktop = useMediaQuery(desktopFirstQuery.desktop);

  const { copyShortLink } = useCopyShortLink({
    successMessage: t('링크 복사 완료'),
  });

  const { togglePublishMutate } = useNotePublishMutation();

  const { trackNoteShareClickEvent } = useTrackNoteShareEvent();

  const handleClick = () => {
    if (isWritable) {
      setOpened(!opened);

      return;
    }
    handleCopy();
  };

  const handleCopy = () => {
    copyShortLink({
      url: UCC_URL.NOTES_ID(Number(courseNoteId)),
      creatorId: profile?.id,
    });

    trackNoteShareClickEvent();
  };

  const handleTogglePublic = (targetIsPublic: boolean) => {
    togglePublishMutate({ isPublic: targetIsPublic, id: Number(courseNoteId) });
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={268}
      withinPortal
      position={!isSmallerThanDesktop ? 'bottom-start' : 'bottom-end'}
      shadow="sm"
    >
      <Popover.Target>
        <Button
          leftSection={isBoolean(isWritable) ? <Icon icon={isWritable ? faShare : faLink} /> : null}
          variant="default"
          color="infgreen"
          size="sm"
          radius="lg"
          onClick={handleClick}
        >
          {t('노트 공유')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={12}>
          <Text size="sm" c="gray.6" fw={400}>
            {t('노트를 공개하고, 다른 사람들과 배움을 공유해 보세요!')}
          </Text>
          <Group>
            <Switch
              label={t('노트 공개')}
              color="infgreen"
              size="sm"
              labelPosition="left"
              onChange={({ currentTarget }) => {
                const { checked } = currentTarget;
                handleTogglePublic(checked);
              }}
              checked={isPublic}
            />
            <Button
              color="infgreen"
              size="sm"
              radius="md"
              ml="auto"
              onClick={handleCopy}
              variant={isPublic ? 'light' : 'default'}
              disabled={!isPublic}
            >
              {t('링크 복사')}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
